import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import * as icon from "@coreui/icons";
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-invoice/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'
import FormFileThumbnail from 'src/components/form/general-input-form/FormFileThumbnail'
import AppCardHeader from 'src/components/card/AppCardHeader'

// helpers
import Axios from '../../helpers/axios'
import { formUrlQuery } from '../../helpers/format-queries'
import { 
  singleProjectsOptionsSelect, 
  singleCountriesOptionsSelect, 
  singleClientsOptionsSelect, 
  singleCourierOptionSelect
} from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'
import {
  formikClientForOtherPage,
  formikProjectForOtherPage
} from '../../helpers/formik'

const EditInvoice = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();
  const params = useParams()
  const { id } = params
  
  const isLoading = useSelector((state) => state.isLoading)
  const [invoiceDetail, setInvoiceDetail] = useState(null)
  const [projectOptions, setProjectOptions] = useState([])
  const [clientOptions, setClientOptions] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, item: {} })
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })
  const [showConfirmAddAttachments, setShowConfirmAddAttachments] = useState({ show: false, item: {} })
  const [showModalCreateNewClient, setShowModalCreateNewClient] = useState({ show: false })
  const [showModalCreateNewProject, setShowModalCreateNewProject] = useState({ show: false })

  const [countryValue, setCountryValue] = useState(null)
  const [countryPhone, setCountryPhone] = useState(null)

  const [openFilePreview, setOpenFilePreview] = useState(false)

  const [searchClientListFilter, setSearchClientListFilter] = useState("");

  const [picList, setPicList] = useState([])
  const [picValue, setPicValue] = useState(null)

  const formik = useFormik({
    initialValues: {
      invoice_name: invoiceDetail?.name,
      invoice_description: invoiceDetail?.description,
      invoice_value: invoiceDetail?.value,
      invoice_tax_value: invoiceDetail?.tax_value,
      invoice_due_date: invoiceDetail?.due_date || '',
      invoice_expected_payment_date: invoiceDetail?.expected_payment_date || '',
      invoice_tax_due_date: invoiceDetail?.tax_due_date || '',
      project_value: invoiceDetail?.project ? {
        label: invoiceDetail?.project?.name,
        value: invoiceDetail?.project?.id
      } : null,
      invoice_is_paid: invoiceDetail?.is_paid ? invoiceDetail.is_paid : false,
      invoice_bupot_required: invoiceDetail?.bupot_required ? invoiceDetail?.bupot_required : false,
      invoice_faktur_required: invoiceDetail?.faktur_required ? invoiceDetail?.faktur_required : false,
      invoice_payment_date: invoiceDetail?.payment_date || '',
      file: invoiceDetail?.attachments || [],
      file_generate_pdf: invoiceDetail?.generate_pdf_attachments || [],
      file_bupot: invoiceDetail?.bupot_url ? [{ file_url: invoiceDetail?.bupot_url, file_original_name: invoiceDetail?.bupot_original_name }] : [],
      file_faktur: invoiceDetail?.faktur_url ? [{ file_url: invoiceDetail?.faktur_url, file_original_name: invoiceDetail?.faktur_original_name }] : [],
      invoice_number: invoiceDetail?.invoice_number || '',
      invoice_is_draft: invoiceDetail?.is_draft ? true : false,
      recipients: invoiceDetail?.recipients?.length > 0 ? invoiceDetail?.recipients : [
        {
          recipient_name: null,
          phone_number: '',
          country_id: '',
          country_value: null,
          recipient_email: '',
        }
      ],
      client_value: invoiceDetail?.client ? {
        label: invoiceDetail?.client?.name,
        value: invoiceDetail?.client?.id
      } : null,
      invoice_shipping_detail: invoiceDetail?.shipping_detail ? {
        ...invoiceDetail?.shipping_detail,
        ...invoiceDetail?.shipping_detail?.document_submission,
        courier: invoiceDetail?.shipping_detail ? singleCourierOptionSelect()?.filter((item) => item.value == invoiceDetail?.shipping_detail?.courier)[0] : null,
        file_document_submission: invoiceDetail?.shipping_detail?.doc_sub_attachments ? invoiceDetail?.shipping_detail?.doc_sub_attachments : []
      } : {
        invoice_id: id,
        is_soft_copy: false,
        is_hard_copy: false,
        recipient: '',
        email_to: '',
        email_date: '',
        courier: null,
        tracking_no: '',
        shipping_date: '',
        terms_invoice: false,
        terms_bast: false,
        terms_po: false,
        terms_faktur_pajak: false,
        terms_pkp: false,
        terms_npwp: false,
        terms_progress_report: false,
        terms_additional_info: '',
      },
      file_shipping: invoiceDetail?.shipping_detail?.shipping_attachments ? invoiceDetail?.shipping_detail?.shipping_attachments : []
    },
    validationSchema: Yup.object().shape({
      invoice_name: Yup.string()
        .required("Please enter invoice name."),
      invoice_description: Yup.string()
        .required("Please enter invoice description."),
      invoice_value: Yup.string()
        .required("Please enter invoice value."),
      invoice_tax_value: Yup.string()
        .required("Please enter invoice tax and other fees."),
      invoice_due_date: Yup.string()
        .required("Please select invoice due date."),
      invoice_expected_payment_date: Yup.string().required("Please select invoice expected payment date."),
      invoice_tax_due_date: Yup.string()
        .required("Please select invoice tax due date."),
      invoice_payment_date: Yup.string()
        .when('invoice_is_paid', {
          is: true,
          then: () => Yup.string().required('Please select invoice payment date')
        }),
      recipients: Yup.array()
        .of(
          Yup.object().shape({
            recipient_name: Yup.string().required("Please enter recipient name."),
            phone_number: Yup.string().required("Please enter recipient contact number."),
            country_value: Yup.string().required("Please select country of recipient."),
            recipient_email: Yup.string().required("Please enter recipient email."),
          })
        ).min(1, 'Please add at least one recipient'),
      client_value: Yup.object().shape({
        label: Yup.string().required('Please select client name.')
      }).required('Please select client name.'),
      invoice_shipping_detail: Yup.object()
      .when(['is_soft_copy', 'is_hard_copy'], {
        is: (is_soft_copy, is_hard_copy) => is_soft_copy == true || is_hard_copy == true,
        then: () => Yup.object().shape({
          is_soft_copy: Yup.boolean(),
          is_hard_copy: Yup.boolean(),
          recipient: Yup.string().when('is_soft_copy', {
            is: true,
            then: (schema) => schema.required('Please enter recipient name.')
          }),
          email_to: Yup.string().when('is_soft_copy', {
            is: true,
            then: (schema) => schema.required('Please enter email to.')
          }),
          email_date: Yup.string().when('is_soft_copy', {
            is: true,
            then: (schema) => schema.required('Please select email date.')
          }),
          courier: Yup.object().when('is_hard_copy', {
            is: true,
            then: () => Yup.object().shape({
              label: Yup.string().required('Please select courier.')
            })
          }),
          tracking_no: Yup.string().when('is_hard_copy', {
            is: true,
            then: (schema) => schema.required('Please enter tracking number.')
          }),
          shipping_date: Yup.string().when('is_hard_copy', {
            is: true,
            then: (schema) => schema.required('Please select shipping date.')
          }),
        }),
      })
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  const formikClient = formikClientForOtherPage({ handleSave: handleSaveNewClient })
  const formikProject = formikProjectForOtherPage({ handleSave: handleSaveNewProject })

  useEffect(() => {
    fetchDetailInvoice()
    fetchAllCountries()
    fetchAllClient()
  }, [id])

  useEffect(() => {
    let queries = {
      search: searchClientListFilter,
      limit: 10,
      offset: 0,
    };
    queries = formUrlQuery(queries);
    if (searchClientListFilter) {
      fetchAllClient(queries)
    } else {
      setClientOptions([])
    }
  }, [
    searchClientListFilter
  ])

  useEffect(() => {
    if (formik.values.client_value?.value) {
      let queries = {
        client_id: formik.values?.client_value?.value,
      }
      queries = formUrlQuery(queries);

      fetchAllProject(queries)
    } else {
      setProjectOptions([])
    }
  }, [
    formik.values.client_value
  ])

  const fetchDetailInvoice = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/tracker/v1/invoice/${id}`,
    })
      .then(({ data }) => {
        const { success, value } = data || {}
        if (success) {
          let invoiceValue = value
          let recipientsValue = invoiceValue?.recipients || []

          if (recipientsValue.length > 0) {
            recipientsValue = recipientsValue?.map((e) => {
              return {
                ...e,
                phone_number: e?.recipient_contact_number,
                country_id: e?.recipient_country_id,
                country_value: JSON.stringify({
                  id: e?.country_value?.id,
                  name: e?.country_value?.name,
                  icon: icon[`cif${e?.country_value?.country_code}`],
                  phone_code: e?.country_value?.phone_code
                })
              }
            })
          }

          invoiceValue.recipients = recipientsValue

          setInvoiceDetail(invoiceValue)

          if (invoiceValue?.shipping_detail?.recipient) {
            setPicValue({
              label: invoiceValue?.shipping_detail?.recipient,
              value: picList?.filter((item) => item.label == invoiceValue?.shipping_detail?.recipient)[0]?.value
            })
          }

          if (invoiceValue?.client?.id) {
            fetchAllPicClient(invoiceValue?.client?.id)
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllProject = (queries) => {
    let url = `/api/tracker/v1/project`
    if (queries) {
      url += `?${queries}`
    }
    Axios({
      method: "get",
      url,
    })
      .then(({ data }) => {
        if (data?.success) {
          setProjectOptions(singleProjectsOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        setCountryOptions(singleCountriesOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const fetchAllClient = (queries) => {
    let url = `/api/tracker/v1/client`
    if (queries) {
      url += `?${queries}`
    }
    Axios({
      method: "get",
      url,
    })
      .then(({ data }) => {
        if (data?.success) {
          setClientOptions(singleClientsOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllPicClient = (clientId) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/tracker/v1/client/pic/options/${clientId}`,
    })
      .then(({ data }) => {
        const { success, value } = data || {}
        if (success) {
          setPicList(value)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleOnDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : ""
    }), false)
  }

  const handleInputValue = (values) => {
    const formData = new FormData()

    formData.append('name', values?.invoice_name)
    formData.append('description', values?.invoice_description)
    formData.append('value', parseFloat(values?.invoice_value?.replace(/,/g, "")))
    formData.append('tax_value', parseFloat(values?.invoice_tax_value?.replace(/,/g, "")))
    formData.append('due_date', values?.invoice_due_date)
    formData.append('expected_payment_date', values?.invoice_expected_payment_date)
    formData.append('is_paid', values?.invoice_is_paid)
    formData.append('is_draft', values?.invoice_is_draft)
    if (values?.invoice_is_paid && values?.invoice_payment_date) {
      formData.append('payment_date', values?.invoice_payment_date)
    }
    formData.append('tax_due_date', values?.invoice_tax_due_date)
    if (values?.file?.length > 0) {
      for (let i = 0; i < values.file.length; i++) {
        if (!values?.file[i]?.file_url && values?.file[i] instanceof File) {
          formData.append('file', values.file[i])
        }
      }
    }
    formData.append('bupot_required', values?.invoice_bupot_required)
    if (values?.file_bupot?.length > 0) {
      for (let i = 0; i < values.file_bupot.length; i++) {
        formData.append('file_bupot', values.file_bupot[i])
      }
    }
    formData.append('faktur_required', values?.invoice_faktur_required)
    if (values?.file_faktur?.length > 0) {
      for (let i = 0; i < values.file_faktur.length; i++) {
        formData.append('file_faktur', values.file_faktur[i])
      }
    }
    if (values?.project_value?.value) {
      formData.append('project_id', values?.project_value?.value)
    }
    if (values.recipients.length > 0) {
      formData.append('recipients', JSON.stringify(values.recipients))
    }
    if (values?.client_value?.value) {
      formData.append('client_id', values?.client_value?.value)
    }
    let dataShippingDetail = {
      is_soft_copy: false,
      is_hard_copy: false,
      recipient: null,
      email_to: null,
      email_date: null,
      tracking_no: null,
      courier: null,
      shipping_date: null,
      document_submission: {
        terms_invoice: values?.invoice_shipping_detail?.terms_invoice || false,
        terms_bast: values?.invoice_shipping_detail?.terms_bast || false,
        terms_po: values?.invoice_shipping_detail?.terms_po || false,
        terms_faktur_pajak: values?.invoice_shipping_detail?.terms_faktur_pajak || false,
        terms_pkp: values?.invoice_shipping_detail?.terms_pkp || false,
        terms_npwp: values?.invoice_shipping_detail?.terms_npwp || false,
        terms_progress_report: values?.invoice_shipping_detail?.terms_progress_report || false,
        terms_additional_info: values?.invoice_shipping_detail?.terms_additional_info || '',
      }
    }
    if (values.invoice_shipping_detail.is_soft_copy) {
      dataShippingDetail = {
        ...dataShippingDetail,
        is_soft_copy: true,
        recipient: values.invoice_shipping_detail?.recipient,
        email_to: values.invoice_shipping_detail?.email_to,
        email_date: values.invoice_shipping_detail?.email_date
      }
    }
    if (values.invoice_shipping_detail.is_hard_copy) {
      dataShippingDetail = {
        ...dataShippingDetail,
        is_hard_copy: true,
        tracking_no: values.invoice_shipping_detail?.tracking_no,
        courier: values.invoice_shipping_detail.courier?.value,
        shipping_date: values.invoice_shipping_detail?.shipping_date
      }
    }
    if (Object.keys(dataShippingDetail)?.length > 0) {
      formData.append('shipping_detail', JSON.stringify(dataShippingDetail))
    }
    if (values?.file_shipping?.length > 0) {
      for (let i = 0; i < values.file_shipping.length; i++) {
        if (!values?.file_shipping[i]?.file_url && values?.file_shipping[i] instanceof File) {
          formData.append('file_shipping', values.file_shipping[i])
        }
      }
    }
    if (values?.invoice_shipping_detail?.file_document_submission?.length > 0) {
      for (let i = 0; i < values.invoice_shipping_detail.file_document_submission.length; i++) {
        if (!values?.invoice_shipping_detail?.file_document_submission[i]?.file_url && values?.invoice_shipping_detail?.file_document_submission[i] instanceof File) {
          formData.append('file_document_submission', values.invoice_shipping_detail.file_document_submission[i])
        }
      }
    }

    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/invoice/update/${id}`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('Invoice has been successfully updated')
          navigate(`/invoice/details/${id}`)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSelectProject = (data) => {
    formik.setValues((values) => ({
      ...values,
      project_value: data
    }), false)
  }

  const handleSelectClient = (data) => {
    formik.setValues((values) => ({
      ...values,
      client_value: data || null,
      project_value: data?.value !== formik.values.client_value?.value || !data ? null : values.project_value
    }), false)
    
    if (data?.value) {
      fetchAllPicClient(data?.value)
    }
  }

  const handleFileInput = (values, type) => {
    const files = values
    const key = type ? type : 'file'

    if (files) {
      switch (key) {
        case 'file_document_submission':
          formik.setValues((prevValues) => ({
            ...prevValues,
            invoice_shipping_detail: {
              ...prevValues.invoice_shipping_detail,
              [key]: [...prevValues.invoice_shipping_detail[key], ...files]
            }
          }), false)
          break;
        default:
          formik.setValues((prevValues) => ({
            ...prevValues,
            [key]: [...prevValues[key], ...files]
          }), false)
          break;
      }
    }

    setShowConfirmAddAttachments({ show: false, item: {} })
  }

  const handleRemoveFile = (index = 0, type) => {
    const key = type ? type : 'file'
    
    let prevValue = key == 'file_document_submission' ? formik.values.invoice_shipping_detail[key][index] : formik.values[key][index]
    let isDeleted = false
    let url = ''

    if (prevValue?.file_url) {
      switch (key) {
        case 'file':
          isDeleted = true
          url = `/api/tracker/v1/invoice/delete/invoiceattachment/${prevValue?.id}`
          break;
        case 'file_bupot':
          isDeleted = true
          url = `/api/tracker/v1/invoice/delete/bupot/${prevValue?.id}`
          break;
        case 'file_faktur':
          isDeleted = true
          url = `/api/tracker/v1/invoice/delete/faktur/${prevValue?.id}`
          break;
        case 'file_shipping':
          isDeleted = true
          url = `/api/tracker/v1/invoice/delete/shipping/${prevValue?.id}`
          break;
        case 'file_generate_pdf':
          isDeleted = true
          url = `/api/tracker/v1/invoice/delete/invoicegeneratepdf/${prevValue?.id}`
          break;
        case 'file_document_submission':
          isDeleted = true
          url = `/api/tracker/v1/invoice/delete/invoicedocumentsubmissionattachment/${prevValue?.id}`
          break;
        default:
          break;
      }
    }

    if (isDeleted) {
      dispatch({
        type: 'SET_IS_LOADING',
        isLoading: true
      })
      Axios({
        method: "delete",
        url,
      })
        .then(({ data }) => {
          if (data?.success) {
            fetchDetailInvoice()
            showSuccess('File has been successfully deleted')
            window.location.reload()
          }
        })
        .catch((err) => {
          console.error(err);
          const errMsg = err?.response?.data?.message
          showError(errMsg)
        })
        .finally(() => {
          dispatch({
            type: 'SET_IS_LOADING',
            isLoading: false
          })
        })
    } else {
      formik.setValues((values) => ({
        ...values,
        [key]: values[key]?.filter((_, i) => i !== index)
      }), false)
    }

    setShowDeleteItem({ show: false, item: {}, index: 0 })
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleCardHeaderButton = () => {
    setShowConfirmAddAttachments({ show: true, item: {} })
  }

  const handleAddInvoiceRecipients = () => {
    formik.setValues((values) => ({
      ...values,
      recipients: [
        ...values.recipients,
        ...[
          {
            recipient_name: null,
            phone_number: '',
            country_id: '',
            country_value: null,
            recipient_email: '',
          }
        ]
      ]
    }), false)
  }

  const handleChangeInvoiceRecipients = (e, informationIndex) => {
    const name = e?.target?.name
    const value = e?.target?.value

    formik.setValues((values) => {
      const newArrayInformation = values.recipients.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: value
          };
        } else {
          return item;
        }
      });

      return {
        ...values,
        recipients: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleSelectCountryInvoiceRecipients = (data, informationIndex) => {
    let edataCountryValue = {
      target: {
        name: 'country_value',
        value: data?.value
      }
    }
    handleChangeInvoiceRecipients(edataCountryValue, informationIndex)
  }

  const handleRemoveInvoiceRecipients = (_, index) => {
    formik.setValues((values) => ({
      ...values,
      recipients: values.recipients.filter((_, itemIndex) => itemIndex !== index)
    }), false)
  }

  const handleSearchClient = (e) => {
    setSearchClientListFilter(e)
  }

  const handleSearchProject = (e) => {
    if (e) {
      const filteredProjectOptions = projectOptions.filter((project) =>
        project.label?.toLowerCase()?.includes(e.toLowerCase())
      );
      setProjectOptions(filteredProjectOptions);
    }
  }

  const handleCheckbox = (e) => {
    const isChecked = e.target.checked
    const name = e.target.name
    formik.setValues((values) => ({
      ...values,
      invoice_shipping_detail: {
        ...values.invoice_shipping_detail,
        [name]: isChecked
      }
    }), false)
  }

  const handleChangeShippingDetail = (e) => {
    const value = e.target.value
    const name = e.target.name
    formik.setValues((values) => ({
      ...values,
      invoice_shipping_detail: {
        ...values.invoice_shipping_detail,
        [name]: value
      }
    }), false)
  }

  const handleDateChangeShippingDetail = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      invoice_shipping_detail: {
        ...values.invoice_shipping_detail,
        [name]: date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : ''
      }
    }), false)
  }

  const handleSelectCourier = (data) => {
    formik.setValues((values) => ({
      ...values,
      invoice_shipping_detail: {
        ...values.invoice_shipping_detail,
        ['courier']: data
      }
    }), false)
  }

  const handleSelectPic = (data) => {
    formik.setValues((values) => ({
      ...values,
      invoice_shipping_detail: {
        ...values.invoice_shipping_detail,
        ['recipient']: data ? data?.value : ''
      }
    }), false)
    
    setPicValue(data ? data : null)
  }

  const handleInputValueNewClient = (values) => {
    const formData = new FormData()

    formData.append('name', values.client_name)
    formData.append('description', values.client_description)
    formData.append('address', values.client_address)
    formData.append('pic_name', values.pic_name)
    formData.append('pic_contact_number', values.phone_number)
    formData.append('pic_country_id', values.pic_country_id)
    formData.append('pic_job_position', values.pic_job_position)
    formData.append('pic_email_address', values.pic_email_address)
    formData.append('pic_department', values.pic_department)
    formData.append('pic_additional_notes', values.pic_additional_notes)
    formData.append('country_id', values.client_country_id)

    return formData
  }

  const handlePostNewClient = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/client/create`,
      data: payload
    })
      .then(({ data }) => {
        const { success } = data || {}
        if (success) {
          showSuccess('New client has been successfully created')
          formik.setValues((values) => ({
            ...values,
            client_value: {
              label: data?.value?.name,
              value: data?.value?.id
            }
          }), false)
          setShowModalCreateNewClient({ show: false })
          fetchAllPicClient(data?.value?.id)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  function handleSaveNewClient(values) {
    const inputPayload = handleInputValueNewClient(values)
    handlePostNewClient(inputPayload)
  }

  const handleInputValueNewProject = (values) => {
    const formData = new FormData()
    formData.append('name', values?.project_name)
    formData.append('description', values?.project_description)
    formData.append('value', parseFloat(values?.project_value?.replace(/,/g, "")))
    formData.append('tax_value', parseFloat(values?.project_tax_value?.replace(/,/g, "")))
    formData.append('start_date', values?.project_start_date)
    formData.append('end_date', values?.project_end_date)
    formData.append('client_id', values?.client_value?.value)
    formData.append('status', 'inprogress')

    return formData
  }

  const handlePostNewProject = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/project/create`,
      data: payload
    })
      .then(({ data }) => {
        const { success } = data || {}
        if (success) {
          showSuccess('New project has been successfully created')
          setProjectOptions((prevOptions) => [
            ...prevOptions,
            {
              label: data?.value?.name,
              value: data?.value?.id
            }
          ])
          formik.setValues((values) => ({
            ...values,
            project_value: {
              label: data?.value?.name,
              value: data?.value?.id
            }
          }), false)
          setShowModalCreateNewProject({ show: false })
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  function handleSaveNewProject(values) {
    const inputPayload = handleInputValueNewProject(values)
    handlePostNewProject(inputPayload)
  }

  const handleShowModalCreate = (name, value) => {
    if (name?.includes('client')) {
      setShowModalCreateNewClient({ show: true })
      formikClient.setValues((values) => ({
        ...values,
        client_name: value
      }), false)
    }
    if (name?.includes('project')) {
      setShowModalCreateNewProject({ show: true })
      formikProject.setValues((values) => ({
        ...values,
        project_name: value,
        client_value: formik.values.client_value
      }), false)
    }
  }

  const handleSelectCountryPhone = (data) => {
    formikClient.setValues((values) => ({
      ...values,
      pic_country_id: data ? JSON.parse(data?.value).id : ''
    }), false)

    setCountryPhone(data? data?.value : null)
  }

  const handleSelectCountry = (data) => {
    if (data?.value) {
      formikClient.setValues((values) => ({
        ...values,
        client_country_id: data ? JSON.parse(data?.value).id : ''
      }), false)

      setCountryValue({
        label: data?.label,
        value: JSON.parse(data?.value)?.id
      })
    } else {
      setCountryValue(null)

      formikClient.setValues((values) => ({
        ...values,
        client_country_id: '',
      }), false)
    }
  }

  const handleOnDateChangeNewProject = (date, name) => {
    formikProject.setValues((values) => ({
      ...values,
      [name]: moment(date).format('YYYY-MM-DD HH:mm:ss')
    }), false)
  }

  const handleSubmitNewClient = () => {
    formikClient.handleSubmit()

    setShowModalCreateNewClient({ show: false })
  }

  const handleSubmitNewProject = () => {
    formikProject.handleSubmit()

    setShowModalCreateNewProject({ show: false })
  }
  
  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showConfirmAddAttachments.show && (
          <AppModalConfirmation 
            confirmationType='add-attachments'
            itemName='file'
            itemDetails={showConfirmAddAttachments.item}
            showModal={showConfirmAddAttachments}
            setShowModal={setShowConfirmAddAttachments}
            errors={formik.errors?.file}
            handleChange={formik.handleChange}
            handleFileInput={handleFileInput}
            headerTitle={`Add Invoice Attachments`}
          />
        )
      }
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`${showDeleteItem?.item?.file_original_name ? showDeleteItem?.item?.file_original_name : showDeleteItem?.item?.name}`}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to edit this invoice?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      {
        showModalCreateNewClient.show && (
          <AppModalConfirmation 
            confirmationType='create-new-client'
            showModal={showModalCreateNewClient}
            setShowModal={setShowModalCreateNewClient}
            headerTitle="Create New Client"
            handleSubmit={handleSubmitNewClient}
            values={formikClient.values}
            errors={formikClient.errors}
            handleChange={formikClient.handleChange}
            countryOptions={countryOptions}
            countryValue={countryValue}
            countryPhone={countryPhone}
            handleSelectCountry={handleSelectCountry}
            handleSelectCountryPhone={handleSelectCountryPhone}
          />
        )
      }
      {
        showModalCreateNewProject.show && (
          <AppModalConfirmation 
            confirmationType='create-new-project'
            showModal={showModalCreateNewProject}
            setShowModal={setShowModalCreateNewProject}
            headerTitle="Create New Project"
            handleSubmit={handleSubmitNewProject}
            values={formikProject.values}
            errors={formikProject.errors}
            handleChange={formikProject.handleChange}
            onDateChange={handleOnDateChangeNewProject}
            clientOptions={clientOptions}
            handleSelectClient={handleSelectClient}
            handleSearchInput={handleSearchClient}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<span className='fs-5 fs-md-4'>Edit Invoice</span>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            onDateChange={handleOnDateChange}
            handleFileInput={handleFileInput}
            projectOptions={projectOptions}
            handleSelectProject={handleSelectProject}
            isShowDropdown={true}
            handleConfirmDelete={setShowDeleteItem}
            handleViewFile={setOpenFilePreview}
            handleAddInvoiceRecipients={handleAddInvoiceRecipients}
            handleChangeInvoiceRecipients={handleChangeInvoiceRecipients}
            handleSelectCountryInvoiceRecipients={handleSelectCountryInvoiceRecipients}
            handleRemoveInvoiceRecipients={handleRemoveInvoiceRecipients}
            countryOptions={countryOptions}
            clientOptions={clientOptions}
            handleSelectClient={handleSelectClient}
            handleSearchInput={handleSearchClient}
            handleSearchProject={handleSearchProject}
            handleCheckbox={handleCheckbox}
            handleChangeShippingDetail={handleChangeShippingDetail}
            handleDateChangeShippingDetail={handleDateChangeShippingDetail}
            courierOptions={singleCourierOptionSelect()}
            handleSelectCourier={handleSelectCourier}
            handleSelectPic={handleSelectPic}
            picList={picList}
            picValue={picValue}
            handleShowModalCreate={handleShowModalCreate}
          />
        }
        lowerBodyContent={
          <>
            <AppCard 
              className='mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<span className='fs-6 fs-md-4'>Generated Invoice</span>}
                />
              }
              bodyContent={
                <>
                  {
                    formik?.values?.file_generate_pdf?.length > 0 ? (
                      <FormFileThumbnail 
                        name='file_generate_pdf'
                        colSize='3'
                        files={formik?.values?.file_generate_pdf}
                        className='justify-content-start align-items-center'
                        handleConfirmDelete={setShowDeleteItem}
                        handleViewFile={setOpenFilePreview}
                      />
                    )
                    : (
                      <>Not Found</>
                    )
                  } 
                </>
              }
            />
            <AppCard 
              className='mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<span className='fs-6 fs-md-4'>Attachments</span>}
                  headerButton="Upload new"
                  handleCardHeaderButton={handleCardHeaderButton}
                />
              }
              bodyContent={
                <>
                  {
                    formik?.values?.file?.length > 0 ? (
                      <FormFileThumbnail 
                        name='file'
                        colSize='3'
                        files={formik?.values?.file}
                        className='justify-content-start align-items-center'
                        handleConfirmDelete={setShowDeleteItem}
                        handleViewFile={setOpenFilePreview}
                      />
                    )
                    : (
                      <>Not Found</>
                    )
                  } 
                </>
              }
            />
          </>
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate(`/invoice/details/${id}`)}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default EditInvoice